/**
 * ป้าย Kanban การผลิต 148.5×90 มม. แนวนอน — เรนเดอร์จาก `print_data` อย่างเดียว
 * ไม่คำนวณใหม่จากที่อื่น (D-170 ข้อ 3: สิ่งที่พิมพ์จริงต้องพิมพ์ซ้ำได้เหมือนเดิมแม้
 * ข้อมูลหลักจะเปลี่ยนไปแล้ว) layout ตามตัวอย่างป้ายของผู้ใช้ (2026-11-04) แต่เส้นกรอบ
 * ครบทุกช่อง: ทุกช่องคลาส `cap-kanban-tag__cell` มีเส้นทึบดำรอบด้าน (ดู desktop.css)
 *
 * **หนึ่งป้ายต่อหนึ่งแพ็คเกจ (D-170 ข้อ 4)**: `tag` รับได้ทั้งป้ายเดี่ยวและรายการป้าย —
 * แต่ละป้ายเป็นหน้าขนาด 148.5×90 มม. ของตัวเอง ปุ่ม "พิมพ์" อยู่นอก loop จึงเรียก `window.print()` ครั้งเดียว
 *
 * **รูปสินค้า**: ดึงเมื่อมีทั้ง `product_id` และ `image_id` ใน print_data เท่านั้น
 * ล้มเหลวทุกกรณี → กรอบว่าง "ไม่มีรูป" (การพิมพ์ต้องไม่ถูกบล็อกด้วยรูป)
 *
 * วันที่แสดงเป็นเวลา Asia/Bangkok เสมอ ไม่ขึ้นกับ timezone ของเบราว์เซอร์
 */

import { useEffect, useState } from 'react';
import { toString as qrCodeToSvgString } from 'qrcode';

import { orDash } from '../products/productView';
import { fetchProductImageBlob } from '../../lib/products/client';
import { tagColorCss, tagColorTextCss } from '../../lib/outputTags/outputTagView';
import type { OutputTag } from '../../lib/outputTags/types';

export interface KanbanTagSheetProps {
  readonly tag: OutputTag | readonly OutputTag[];
  readonly onClose?: () => void;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

const BANGKOK = 'Asia/Bangkok';

const bangkokFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: BANGKOK,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function bangkokParts(iso: string | null): Record<string, string> | null {
  if (iso === null) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const map: Record<string, string> = {};
  for (const part of bangkokFormatter.formatToParts(date)) {
    map[part.type] = part.value;
  }
  return map;
}

/** dd/MM/yyyy (Asia/Bangkok) หรือ "—" */
function formatBangkokDate(iso: string | null): string {
  const p = bangkokParts(iso);
  return p === null ? '—' : `${p.day}/${p.month}/${p.year}`;
}

/** dd/MM/yyyy HH:mm:ss (Asia/Bangkok) หรือ "—" */
function formatBangkokDateTime(iso: string | null): string {
  const p = bangkokParts(iso);
  return p === null ? '—' : `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}:${p.second}`;
}

/** NUMERIC string without trailing zeros ("30.0000" → "30", "12.5000" → "12.5"); string math, no Number() */
function hasText(value: string | null): value is string {
  return value !== null && value.trim() !== '';
}

function trimDecimal(value: string | null): string | null {
  if (value === null || !value.includes('.')) return value;
  return value.replace(/0+$/, '').replace(/\.$/, '');
}

export function KanbanTagSheet({ tag, onClose, apiBaseUrl, fetchImpl }: KanbanTagSheetProps) {
  const tags = Array.isArray(tag) ? tag : [tag];

  return (
    <div className="cap-kanban-tag-wrap" data-testid="output-tag-preview">
      <div className="cap-kanban-tag-controls">
        <button
          type="button"
          className="cap-button cap-button--primary"
          data-testid="output-tag-print-button"
          onClick={() => {
            window.print();
          }}
        >
          พิมพ์
        </button>
        {onClose ? (
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="output-tag-preview-close"
            onClick={onClose}
          >
            ปิดตัวอย่าง
          </button>
        ) : null}
      </div>

      {tags.map((oneTag, index) => (
        <KanbanTagSheetPage
          key={oneTag.tag_code}
          tag={oneTag}
          pageBreakAfter={index < tags.length - 1}
          apiBaseUrl={apiBaseUrl}
          fetchImpl={fetchImpl}
        />
      ))}
    </div>
  );
}

function useTagImage(
  productId: string | null,
  imageId: string | null,
  apiBaseUrl: string | undefined,
  fetchImpl: typeof fetch | undefined,
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    setUrl(null);
    if (productId === null || imageId === null) {
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const result = await fetchProductImageBlob(productId, imageId, {
          signal: controller.signal,
          ...(apiBaseUrl !== undefined ? { apiBaseUrl } : {}),
          ...(fetchImpl !== undefined ? { fetchImpl } : {}),
        });
        if (result.status === 'ready' && !controller.signal.aborted) {
          objectUrl = URL.createObjectURL(result.blob);
          setUrl(objectUrl);
        }
      } catch {
        // รูปล้มเหลว → กรอบว่าง ไม่บล็อกการพิมพ์
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [productId, imageId, apiBaseUrl, fetchImpl]);

  return url;
}

const CELL = 'cap-kanban-tag__cell';

function KanbanTagSheetPage({
  tag,
  pageBreakAfter,
  apiBaseUrl,
  fetchImpl,
}: {
  readonly tag: OutputTag;
  readonly pageBreakAfter: boolean;
  readonly apiBaseUrl: string | undefined;
  readonly fetchImpl: typeof fetch | undefined;
}) {
  const data = tag.print_data;
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const imageUrl = useTagImage(data.product_id, data.image_id, apiBaseUrl, fetchImpl);

  useEffect(() => {
    let active = true;
    setQrSvg(null);
    if (data.qr === null || data.qr.trim() === '') {
      return;
    }
    void qrCodeToSvgString(data.qr, { type: 'svg', margin: 0 }).then((svg) => {
      if (active) {
        setQrSvg(svg);
      }
    });
    return () => {
      active = false;
    };
  }, [data.qr]);

  const qty = trimDecimal(data.tag_qty ?? data.qty_per_pkg);

  return (
    <div
      className="cap-kanban-tag"
      data-testid="output-tag-sheet"
      style={pageBreakAfter ? { breakAfter: 'page' } : undefined}
    >
      <div
        className={`${CELL} cap-kanban-tag__color`}
        data-testid="output-tag-color"
        style={{
          backgroundColor: tagColorCss(data.tag_color),
          color: tagColorTextCss(data.tag_color),
        }}
      >
        {orDash(data.tag_color)}
      </div>

      <div className={`${CELL} cap-kanban-tag__titlebox`} data-testid="output-tag-titlebox">
        <div className="cap-kanban-tag__title" data-testid="output-tag-title">
          CAP {data.plant_code ?? tag.plant_code ?? '—'} KANBAN [v{tag.tag_schema_version ?? 1}]
        </div>
        <div className="cap-kanban-tag__dates">
          <div data-testid="output-tag-issue">ISSUE: {formatBangkokDate(data.issued_at)}</div>
          <div data-testid="output-tag-print">PRINT: {formatBangkokDateTime(data.printed_at)}</div>
          {data.reprint_of !== null ? (
            <div className="cap-kanban-tag__reprint" data-testid="output-tag-reprint-of">
              REPRINT of {data.reprint_of}
            </div>
          ) : null}
        </div>
      </div>

      <div className={`${CELL} cap-kanban-tag__imagebox`} data-testid="output-tag-image">
        {imageUrl !== null ? (
          <img src={imageUrl} alt={data.part_no ?? 'product'} data-testid="output-tag-image-img" />
        ) : (
          <span data-testid="output-tag-image-placeholder">ไม่มีรูป</span>
        )}
      </div>

      <div className={`${CELL} cap-kanban-tag__partbox`}>
        <div className="cap-kanban-tag__label">PART NO.:</div>
        <div className="cap-kanban-tag__big" data-testid="output-tag-part-no">
          {orDash(data.part_no)}
        </div>
        <div className="cap-kanban-tag__name" data-testid="output-tag-part-name">
          {orDash(data.part_name)}
        </div>
      </div>

      <div className={`${CELL} cap-kanban-tag__modelbox`}>
        <div className="cap-kanban-tag__label">MODEL:</div>
        <div className="cap-kanban-tag__big" data-testid="output-tag-model">
          {hasText(data.customer_code) ? (
            <div data-testid="output-tag-customer-code">{data.customer_code}</div>
          ) : null}
          <div data-testid="output-tag-model-name">{orDash(data.model)}</div>
        </div>
      </div>

      <div className={`${CELL} cap-kanban-tag__c1 cap-kanban-tag__r1`} data-testid="output-tag-process">
        <div className="cap-kanban-tag__label">PROCESS:</div>
        <div className="cap-kanban-tag__value" data-testid="output-tag-line-code">
          {orDash(data.line)}
        </div>
      </div>
      <div className={`${CELL} cap-kanban-tag__c2 cap-kanban-tag__r1`} data-testid="output-tag-line">
        <div className="cap-kanban-tag__label">PROD LINE:</div>
        <div className="cap-kanban-tag__value">{orDash(data.line_name)}</div>
      </div>
      <div className={`${CELL} cap-kanban-tag__c3 cap-kanban-tag__r1`} data-testid="output-tag-dates">
        <div className="cap-kanban-tag__label">START/REQ. DATE:</div>
        <div className="cap-kanban-tag__value">
          <div data-testid="output-tag-start-date">{formatBangkokDate(data.start_date)}</div>
          <div data-testid="output-tag-required-date">{formatBangkokDate(data.required_date)}</div>
        </div>
      </div>
      <div className={`${CELL} cap-kanban-tag__c4 cap-kanban-tag__r1`} data-testid="output-tag-pkg-type">
        <div className="cap-kanban-tag__label">BOX SIZE</div>
        <div className="cap-kanban-tag__value">{orDash(data.pkg_type)}</div>
      </div>

      <div className={`${CELL} cap-kanban-tag__c1 cap-kanban-tag__r2`} data-testid="output-tag-plan-ref">
        <div className="cap-kanban-tag__label">PLAN REF:</div>
        <div className="cap-kanban-tag__value cap-kanban-tag__value--small">{orDash(data.order_no)}</div>
      </div>
      <div className={`${CELL} cap-kanban-tag__c2 cap-kanban-tag__r2`} data-testid="output-tag-store-location">
        <div className="cap-kanban-tag__label">LOCATION TO:</div>
        <div className="cap-kanban-tag__value">
          <div>{orDash(data.store_location)}</div>
          <div data-testid="output-tag-shelf-code">
            [ {hasText(data.shelf_code) ? data.shelf_code.trim() : 'FREE'} ]
          </div>
        </div>
      </div>
      <div className={`${CELL} cap-kanban-tag__c3 cap-kanban-tag__r2`} data-testid="output-tag-lot-no">
        <div className="cap-kanban-tag__label">LOT NO:</div>
        <div className="cap-kanban-tag__value cap-kanban-tag__value--small">{orDash(data.lot_no)}</div>
      </div>
      <div className={`${CELL} cap-kanban-tag__c4 cap-kanban-tag__r2`} data-testid="output-tag-qty-per-pkg">
        <div className="cap-kanban-tag__label">PCS / BOX</div>
        <div className="cap-kanban-tag__value cap-kanban-tag__value--big">
          {orDash(qty)}
          {data.qty_uom !== null ? <small> {data.qty_uom}</small> : null}
        </div>
      </div>

      <div className="cap-kanban-tag__side">
        <div className={`${CELL} cap-kanban-tag__package`} data-testid="output-tag-package">
          {data.package_seq !== null && data.package_count !== null
            ? `[${data.package_seq}/${data.package_count}]`
            : '—'}
        </div>
        <div className={`${CELL} cap-kanban-tag__running-no`} data-testid="output-tag-running-no">
          {orDash(data.tag_code)}
        </div>
        <div className={`${CELL} cap-kanban-tag__qr`} data-testid="output-tag-qr">
          {qrSvg !== null ? (
            // SVG มาจาก `qrcode` ที่เราเรียกเองด้วยข้อความของ print_data ใบนี้ — ไม่ใช่ข้อความจากผู้ใช้
            <div className="cap-kanban-tag__qr-image" dangerouslySetInnerHTML={{ __html: qrSvg }} />
          ) : (
            <span className="cap-kanban-tag__qr-placeholder">—</span>
          )}
        </div>
      </div>
    </div>
  );
}
